/**
 * Browser-side persistence for Deputy compound alias maps.
 *
 * Alias records contain display labels that the Orchestrator must not receive,
 * so callers must never attach these records to wire snapshots or decisions.
 *
 * @module deputy/compoundAliasStore
 */

const DB_NAME = 'bmaps_deputy_aliases';
const DB_VERSION = 2;
const STORE_NAME = 'workspace_alias_maps';
const SESSION_STORE_NAME = 'session_alias_bindings';

function resolveIndexedDB(indexedDBOverride) {
    if (indexedDBOverride) return indexedDBOverride;
    if (typeof globalThis !== 'undefined' && globalThis.indexedDB) return globalThis.indexedDB;
    return null;
}

function openDB(indexedDBOverride) {
    return new Promise((resolve, reject) => {
        const idb = resolveIndexedDB(indexedDBOverride);
        if (!idb || typeof idb.open !== 'function') {
            resolve(null);
            return;
        }

        try {
            const request = idb.open(DB_NAME, DB_VERSION);
            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME, { keyPath: 'workspaceFingerprint' });
                }
                if (!db.objectStoreNames.contains(SESSION_STORE_NAME)) {
                    db.createObjectStore(SESSION_STORE_NAME, { keyPath: 'sessionId' });
                }
            };
            request.onsuccess = (event) => resolve(event.target.result);
            request.onerror = () => reject(request.error);
        } catch (err) {
            reject(err);
        }
    });
}

function runStoreOperation(db, storeName, mode, fn) {
    return new Promise((resolve, reject) => {
        if (!db) {
            resolve(null);
            return;
        }
        try {
            const tx = db.transaction(storeName, mode);
            const store = tx.objectStore(storeName);
            const request = fn(store);
            let requestResult = null;
            if (request) {
                request.onsuccess = () => {
                    requestResult = request.result;
                };
                request.onerror = () => reject(request.error);
            }
            tx.oncomplete = () => resolve(request ? requestResult : true);
            tx.onerror = () => reject(tx.error);
        } catch (err) {
            reject(err);
        }
    });
}

export function createCompoundAliasStore({ indexedDB: indexedDBOverride } = {}) {
    async function getAliasMap(workspaceFingerprint) {
        if (!workspaceFingerprint) return null;
        try {
            const db = await openDB(indexedDBOverride);
            return await runStoreOperation(db, STORE_NAME, 'readonly', (store) => (
                store.get(workspaceFingerprint)
            ));
        } catch (_) {
            return null;
        }
    }

    async function putAliasMap(record) {
        if (!record || !record.workspaceFingerprint) return false;
        try {
            const db = await openDB(indexedDBOverride);
            const result = await runStoreOperation(db, STORE_NAME, 'readwrite', (store) => (
                store.put(record)
            ));
            return result !== null;
        } catch (_) {
            return false;
        }
    }

    async function clearAliasMap(workspaceFingerprint) {
        if (!workspaceFingerprint) return false;
        try {
            const db = await openDB(indexedDBOverride);
            const result = await runStoreOperation(db, STORE_NAME, 'readwrite', (store) => (
                store.delete(workspaceFingerprint)
            ));
            return result !== null;
        } catch (_) {
            return false;
        }
    }

    async function getSessionFingerprint(sessionId) {
        if (!sessionId) return null;
        try {
            const db = await openDB(indexedDBOverride);
            const record = await runStoreOperation(
                db,
                SESSION_STORE_NAME,
                'readonly',
                (store) => store.get(sessionId),
            );
            return record && typeof record.workspaceFingerprint === 'string'
                ? record.workspaceFingerprint
                : null;
        } catch (_) {
            return null;
        }
    }

    async function putSessionFingerprint(sessionId, workspaceFingerprint) {
        if (!sessionId || !workspaceFingerprint) return false;
        try {
            const db = await openDB(indexedDBOverride);
            const result = await runStoreOperation(
                db,
                SESSION_STORE_NAME,
                'readwrite',
                (store) => store.put({ sessionId, workspaceFingerprint, updatedAt: Date.now() }),
            );
            return result !== null;
        } catch (_) {
            return false;
        }
    }

    async function clearSessionFingerprint(sessionId) {
        if (!sessionId) return false;
        try {
            const db = await openDB(indexedDBOverride);
            const result = await runStoreOperation(
                db,
                SESSION_STORE_NAME,
                'readwrite',
                (store) => store.delete(sessionId),
            );
            return result !== null;
        } catch (_) {
            return false;
        }
    }

    return {
        getAliasMap,
        putAliasMap,
        clearAliasMap,
        getSessionFingerprint,
        putSessionFingerprint,
        clearSessionFingerprint,
    };
}

export const defaultCompoundAliasStore = createCompoundAliasStore();
